import { isolateDirection } from "@frontend-shared/lib";
import { resolveRunFailedNotificationView } from "@frontend-shared/utils";
import { AlertTriangle } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";
import type { IRunFailedCardProps } from "./types";

// NOTE: NSAI-7884 — one card for AGENT_RUN_FAILED and WORKFLOW_RUN_FAILED (same
// layout and CTA, only the sentence and the run history it opens differ), so
// the two never drift and Sonar sees no duplicate. The sentence comes from the
// shared resolver, which mobile uses too.
const RunFailedCardComponent = ({
  uuid,
  notification,
  createdAt,
  isRead,
  onViewRuns,
}: IRunFailedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");
  const view = useMemo(
    () => resolveRunFailedNotificationView(notification),
    [notification],
  );

  // NOTE: NSAI-7883 — `?runs=` resolves a workflow too (the Workflows tab
  // opens its own history), so both types take the same CTA.
  const handleViewRuns = useCallback((): void => {
    onViewRuns(view.automationUuid, view.projectUuid);
  }, [view, onViewRuns]);

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId={notification.type}
      createdAt={createdAt}
      isRead={isRead}
      icon={<AlertTriangle className="h-5 w-5 text-destructive" />}
      iconBgClassName="bg-destructive/10"
      ctaLabel={t("view_runs")}
      onCtaClick={handleViewRuns}
      body={
        <div className="space-y-1">
          <p className="font-medium text-foreground">{t(view.titleKey)}</p>
          {/* NOTE: NSAI-4800 — isolated so a Latin name's trailing
              punctuation stays inside its quotes in an RTL sentence, as on
              the mobile card. */}
          <p>
            {t(view.bodyKey, {
              automationName: isolateDirection(view.automationName),
            })}
          </p>
          {/* NOTE: Already clamped server-side (summarizeRunError); the shell's
              `[overflow-wrap:anywhere]` keeps a long token from widening the
              drawer. Isolated because the error stays an LTR sentence inside
              an RTL line. */}
          {view.error && (
            <p
              className="text-xs text-muted-foreground"
              data-testid={`notification-card-${notification.type}-last-error`}
            >
              {t(view.lastErrorKey, { error: isolateDirection(view.error) })}
            </p>
          )}
        </div>
      }
    />
  );
};

export const RunFailedCard = memo(RunFailedCardComponent);
